import * as React from 'react';
import { tracker } from '@aether/tracking';
import { Alert } from '@aether/ui/Alert';
import { Button } from '@aether/ui/Button';
import { Card } from '@aether/ui/Card';
import { CheckboxGroup } from '@aether/ui/CheckboxGroup';
import { Heading } from '@aether/ui/Heading';
import { Icon } from '@aether/ui/Icon';
import { SelectField } from '@aether/ui/SelectField';
import { TextField } from '@aether/ui/TextField';
import { captchaSchema, displayNameSchema, emailSchema } from '@aether/validation/user';
import { zodResolver } from '@hookform/resolvers/zod';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router';
import * as z from 'zod';

import { ApiErrorAlert } from '@/components/ApiErrorAlert';
import { PageSeoTitle } from '@/components/PageSeoTitle';
import { PoweredBy } from '@/components/PoweredBy';
import { graphql, useMutation } from '@/graphql';
import { useAbilityContext, useAuth } from '@/utils/auth';

import { AuthLink } from './components/AuthLink';
import { countryOptions } from './Register/Register';

const companyTypes = [
  'asset_owner',
  'asset_manager',
  'financial_advisor',
  'wealth_manager',
  'family_office',
  'educational_institution',
  'other',
] as const;
const companyTypeLabels: Record<(typeof companyTypes)[number], string> = {
  asset_owner: 'Asset owner',
  asset_manager: 'Asset manager',
  financial_advisor: 'Financial advisor',
  wealth_manager: 'Wealth manager',
  family_office: 'Family office',
  educational_institution: 'Educational institution',
  other: 'Other',
};
const mainAssetClasses = ['equity_single_stock', 'equity_etf', 'fixed_income', 'fx', 'commodities', 'other'] as const;
const mainAssetClassLabels: Record<(typeof mainAssetClasses)[number], string> = {
  equity_single_stock: 'Equity (single stock)',
  equity_etf: 'Equity (ETF / Mutual funds)',
  fixed_income: 'Fixed income',
  fx: 'FX',
  commodities: 'Commodities',
  other: 'Other',
};

const features = [
  'improve_performance',
  'build_new_portfolio',
  'analytics',
  'sustainability',
  'custom_factsheets',
  'monitoring_and_alerts',
  'other',
] as const;

const featureLabels: Record<(typeof features)[number], string> = {
  improve_performance: 'Use AI to improve portfolio returns',
  build_new_portfolio: 'Use AI to build new strategies',
  analytics: 'Analytics and attribution',
  sustainability: 'Integrate sustainability (ESG, climate, etc.)',
  custom_factsheets: 'Custom factsheets and/or regulatory reporting',
  monitoring_and_alerts: 'Monitoring and alerts',
  other: 'Other',
};

const formValuesSchema = z
  .object({
    name: displayNameSchema,
    email: emailSchema,
    organizationName: z.string().trim().min(1, { message: 'Please enter your company name' }),
    organizationType: z.enum(companyTypes, {
      required_error: 'Please select an organization type',
    }),
    organizationOther: z.string().trim().optional(),
    assetClasses: z.array(z.enum(mainAssetClasses)).min(1, { message: 'Please select at least one asset class' }),
    assetClassesOther: z.string().trim().optional(),
    features: z.array(z.enum(features)),
    featuresOther: z.string().trim().optional(),
    captchaResponse: captchaSchema,
    country: z.string().trim().min(2, { message: 'Please select a country' }),
  })
  .refine(
    data => {
      if (data.organizationType === 'other') {
        return data.organizationOther && data.organizationOther.trim().length > 0;
      }
      return true;
    },
    {
      message: 'Please specify the organization type',
      path: ['organizationOther'],
    }
  )
  .refine(
    data => {
      if (data.assetClasses.includes('other')) {
        return data.assetClassesOther && data.assetClassesOther.trim().length > 0;
      }
      return true;
    },
    {
      message: 'Please specify the other asset class(es) you trade',
      path: ['assetClassesOther'],
    }
  )
  .refine(
    data => {
      if (data.features.includes('other')) {
        return data.featuresOther && data.featuresOther.trim().length > 0;
      }
      return true;
    },
    {
      message: 'Please specify the other feature(s) you are interested in',
      path: ['featuresOther'],
    }
  );

export function RequestAccess() {
  const [error, setError] = React.useState<any>();
  const turnstileRef = React.useRef<TurnstileInstance | undefined>();
  const [params] = useSearchParams();
  const [requestAccess, { data }] = useMutation(requestAccessMutation);
  const { user, currentOrganization } = useAuth();
  const navigate = useNavigate();
  const ability = useAbilityContext();
  const form = useForm<z.infer<typeof formValuesSchema>>({
    resolver: zodResolver(formValuesSchema),
    defaultValues: {
      name: '',
      email: '',
      organizationName: '',
      organizationOther: '',
      assetClasses: [],
      assetClassesOther: '',
      features: [],
      featuresOther: '',
      captchaResponse: '',
    },
  });

  const onSubmit = async (values: z.infer<typeof formValuesSchema>) => {
    setError(undefined);
    try {
      tracker.capture('request_access_started', values);
      await requestAccess({ variables: { input: values } });
      tracker.identify(values.email);
      tracker.capture('request_access_success');
    } catch (e: any) {
      setError(e);
    }
  };

  if (user && currentOrganization?.planSummary?.featureAccess !== 'public') {
    if (!user.emailVerified) {
      setTimeout(() => {
        navigate('/auth/email-verification', { replace: true });
      });
    } else if (user.organizations.length === 0) {
      navigate('/create-organization', { replace: true });
    } else {
      let redirectTo = params.get('redirectTo') || '/welcome';
      if (ability.cannot('read', 'App')) redirectTo = '/auth/unauthorized';

      setTimeout(() => {
        if (redirectTo.includes('http')) window.location.href = decodeURIComponent(redirectTo);
        else navigate(decodeURIComponent(redirectTo));
      });
    }
    return <></>;
  }

  return (
    <div className="grid grid-cols-1 gap-8 md:grid-cols-2 md:gap-0">
      <div className="bg-gray-900 text-white">
        <div className="mx-auto mb-6 mt-12 w-fit">
          <img src="/images/dragon.svg" alt="Arabesque AI" className="m-auto mr-2 inline-block w-[33px] align-middle" />
          <div className="inline-block align-middle">
            <div className="whitespace-nowrap text-[18px] font-semibold leading-[22px] text-gray-200">AI Dragon</div>
            <PoweredBy className="leading-3" />
          </div>
        </div>
        <div className="max-w-2xl space-y-10 px-4 py-8 md:mx-auto md:px-8">
          <Heading level={2} className="text-2xl font-bold text-white">
            Welcome to AI Dragon.
          </Heading>
          <p className="text-xl">
            The Dragon is built on a foundation of over 50,000 public equity funds and share classes. It monitors more
            than 100,000 stocks globally, ingests over 22 billion data points daily, and delivers analytics,
            predictions, and optimization tools.
          </p>
          <p className="text-xl">
            After three years of live testing on all our funds and selected clients, we are excited to begin a phased
            rollout, starting with professional equity portfolio managers.
          </p>
          <p className="text-xl">
            We are grateful for your feedback to help us improve the platform to better serve your needs.
          </p>
          <p className="text-xl">Thank you for joining us on this journey.</p>
          <p className="text-xl italic">The AI Dragon team.</p>
        </div>
      </div>
      <Card className="w-full max-w-2xl px-4 md:m-20 md:mx-auto md:px-8">
        <PageSeoTitle title="Request access" />

        <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-6">
          <Heading level={2} className="text-2xl font-bold">
            Request access
          </Heading>
          <p className="text-grey-500">
            Already have an account?{' '}
            <AuthLink
              to={`/auth/login?redirectTo=${encodeURIComponent(params.get('redirectTo') || '')}`}
              className="whitespace-nowrap"
            >
              Go to sign-in.
            </AuthLink>
          </p>
          <Controller
            control={form.control}
            name="name"
            render={({ field, fieldState: { error } }) => (
              <TextField
                id="name"
                label="Your name"
                {...field}
                placeholder="Jane Doe"
                // iconBefore="ic:outline-person"
                errorMessage={error?.message}
              />
            )}
          />
          <Controller
            control={form.control}
            name="email"
            render={({ field, fieldState: { error } }) => (
              <TextField
                id="email"
                label="Professional email address"
                {...field}
                // iconBefore="ic:outline-email"
                inputMode="email"
                placeholder="you@company.com"
                errorMessage={error?.message}
              />
            )}
          />
          <Controller
            control={form.control}
            name="organizationName"
            render={({ field, fieldState: { error } }) => (
              <TextField
                id="organizationName"
                // iconBefore="ic:outline-business"
                label="Organization name"
                placeholder="Organization name"
                {...field}
                errorMessage={error?.message}
              />
            )}
          />
          <div className="space-y-1">
            <Controller
              control={form.control}
              name="organizationType"
              render={({ field, fieldState: { error } }) => (
                <SelectField
                  id="organizationType"
                  label="Organization type"
                  {...field}
                  onValueChange={field.onChange}
                  errorMessage={error?.message}
                  options={companyTypes.map(type => ({
                    label: companyTypeLabels[type],
                    value: type,
                  }))}
                />
              )}
            />
            {form.watch('organizationType') === 'other' && (
              <Controller
                control={form.control}
                name="organizationOther"
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    id="organizationOther"
                    wrapperClassName="ml-1.5"
                    fieldGroupClassName="ml-4"
                    errorClassName="ml-4"
                    label={
                      <>
                        <Icon
                          icon="ic:outline-subdirectory-arrow-right"
                          className="-mt-1 mr-0.5 inline text-sm text-grey-500"
                        />
                        How would you describe your organization?
                      </>
                    }
                    {...field}
                    errorMessage={error?.message}
                  />
                )}
              />
            )}
          </div>
          <Controller
            control={form.control}
            name="country"
            render={({ field: { onChange, ...field }, fieldState: { error } }) => (
              <SelectField
                id="country"
                label="In which country is your organization/branch based?"
                {...field}
                onValueChange={onChange}
                isFilterable
                options={countryOptions}
                placeholder="Select a country"
                // iconBefore="ic:outline-flag"
                contentClassName="max-h-[408px]"
                labelClassName="inline-flex items-center gap-1"
                errorMessage={error?.message}
              />
            )}
          />
          <div className="space-y-1">
            <Controller
              control={form.control}
              name="assetClasses"
              render={({ field, fieldState: { error } }) => (
                <CheckboxGroup.Group
                  label="What asset classes do you mainly trade?"
                  {...field}
                  onValueChange={field.onChange}
                  errorMessage={error?.message}
                  wrapperClassName=""
                  labelClassName="text-sm mb-2"
                >
                  {mainAssetClasses.map(assetClass => (
                    <CheckboxGroup.Item
                      key={assetClass}
                      value={assetClass}
                      className="flex items-center gap-1.5 text-sm"
                    >
                      {mainAssetClassLabels[assetClass]}
                    </CheckboxGroup.Item>
                  ))}
                </CheckboxGroup.Group>
              )}
            />
            {form.watch('assetClasses').includes('other') && (
              <Controller
                control={form.control}
                name="assetClassesOther"
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    id="assetClassesOther"
                    wrapperClassName="ml-1.5"
                    fieldGroupClassName="ml-4"
                    errorClassName="ml-4"
                    label={
                      <>
                        <Icon
                          icon="ic:outline-subdirectory-arrow-right"
                          className="-mt-1 mr-0.5 inline text-sm text-grey-500"
                        />
                        What other asset classes do you trade?
                      </>
                    }
                    {...field}
                    errorMessage={error?.message}
                  />
                )}
              />
            )}
          </div>
          <div className="space-y-1">
            <Controller
              control={form.control}
              name="features"
              render={({ field, fieldState: { error } }) => (
                <CheckboxGroup.Group
                  label="Which features are you interested in?"
                  labelClassName="text-sm mb-2"
                  {...field}
                  onValueChange={field.onChange}
                  errorMessage={error?.message}
                >
                  {features.map(feature => (
                    <CheckboxGroup.Item key={feature} value={feature} className="flex items-center gap-1.5 text-sm">
                      {featureLabels[feature]}
                    </CheckboxGroup.Item>
                  ))}
                </CheckboxGroup.Group>
              )}
            />
            {form.watch('features').includes('other') && (
              <Controller
                control={form.control}
                name="featuresOther"
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    id="featuresOther"
                    wrapperClassName="ml-1.5"
                    fieldGroupClassName="ml-4"
                    errorClassName="ml-4"
                    label={
                      <>
                        <Icon
                          icon="ic:outline-subdirectory-arrow-right"
                          className="-mt-1 mr-0.5 inline text-sm text-grey-500"
                        />
                        What other features are you interested in?
                      </>
                    }
                    {...field}
                    errorMessage={error?.message}
                  />
                )}
              />
            )}
          </div>
          <Controller
            control={form.control}
            name="captchaResponse"
            render={({ field, fieldState: { error } }) => (
              <>
                <Turnstile
                  ref={turnstileRef}
                  siteKey={process.env.NX_CF_TURNSTILE_SITE_KEY || ''}
                  options={{ theme: 'light' }}
                  onSuccess={field.onChange}
                  onError={() => {
                    field.onChange('');
                    // @TODO: use react hook forms to set error
                    setError({ message: 'Captcha validation failed' });
                  }}
                  onExpire={() => {
                    field.onChange('');
                    turnstileRef.current?.reset();
                  }}
                />
                <span className="text-sm text-error-500">{error?.message}</span>
              </>
            )}
          />
          {error && <ApiErrorAlert error={error} />}
          <div>
            <Button
              data-testid="ac-register-send"
              loading={form.formState.isSubmitting}
              loadingText="Sending..."
              disabled={Boolean(data?.requestAccess)}
              type="submit"
            >
              Request access
            </Button>
          </div>
          {data?.requestAccess && (
            <Alert level="success">
              Thank you! You will shortly receive an email confirming your access request, and an invitation as soon as
              your request gets approved.
            </Alert>
          )}
        </form>
      </Card>
    </div>
  );
}

export const requestAccessMutation = graphql(`
  mutation RequestAccess($input: RequestAccessInput!) {
    requestAccess(input: $input)
  }
`);
